import { getInitials } from '@awpmarine/core/utils';
import { LinkedInV2Logo } from '@awpmarine/ui/components/logos';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';

import { ContactCta, Eyebrow, Photo } from '@/components/marketing';
import SeafarerOnDeckImg from '@/images/marketing/seafarer-on-deck.jpg';
import { pageHead } from '@/lib/head';
import { blurBackground } from '@/lib/images';
import { languageTag } from '@/lib/locale';
import { ogImage } from '@/lib/open-graph';
import { m } from '@/paraglide/messages';
import { stats } from '@/site-config';

import { team } from './-data';

export const metadata = () => ({ title: m.team_title(), description: m.team_description() });

export const Route = createFileRoute('/team/')({
  head: () => pageHead({ ...metadata(), canonical: '/team', image: ogImage('/team') }),
  component: TeamPage,
});

// Message functions, read when the page renders so the text follows its locale.
const inspectorPoints = [
  m.team_point_experience,
  m.team_point_inspections,
  m.team_point_reviewed,
  m.team_point_vessels,
];

function TeamPage() {
  return (
    <>
      <section className='mx-auto w-full max-w-310 px-5 py-14 text-center sm:px-8 sm:pt-20 sm:pb-16'>
        <Eyebrow>{m.team_eyebrow()}</Eyebrow>
        <h1 className='mx-auto mb-5 max-w-190 text-[clamp(38px,4.6vw,58px)] leading-[1.06] font-bold tracking-[-0.02em]'>
          {m.team_heading()}
        </h1>
        <p className='text-body mx-auto max-w-150 text-[19px] leading-[1.65]'>{m.team_lead()}</p>
      </section>

      <section className='mx-auto w-full max-w-310 px-5 pb-14 sm:px-8 sm:pb-26'>
        <div className='grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] sm:gap-x-6 sm:gap-y-10'>
          {team.map((member) => (
            <div key={member.name} className='flex flex-col gap-3.5'>
              <div
                className='bg-mist relative aspect-4/5 overflow-hidden rounded-[20px]'
                style={blurBackground(member.photo)}
              >
                {member.photo ? (
                  <img
                    src={member.photo}
                    alt={member.name}
                    loading='lazy'
                    className='absolute inset-0 size-full object-cover'
                  />
                ) : (
                  <span className='text-subtle flex h-full items-center justify-center text-3xl font-semibold'>
                    {getInitials(member.name)}
                  </span>
                )}
              </div>
              <div className='flex items-center justify-between gap-3'>
                <span className='min-w-0'>
                  <span className='block text-xl font-bold'>{member.name}</span>
                  <span className='text-subtle'>{member.role()}</span>
                </span>
                {member.linkedin && (
                  <a
                    href={member.linkedin}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={m.team_linkedin_label({ name: member.name })}
                    className='bg-mist flex size-10 shrink-0 items-center justify-center rounded-full text-[#0a66c2]'
                  >
                    <LinkedInV2Logo className='size-4.5' />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className='bg-navy text-white'>
        <div className='mx-auto flex w-full max-w-310 flex-wrap items-stretch gap-14 px-5 py-14 sm:px-8 sm:py-24'>
          <div className='min-w-0 flex-[1_1_440px] self-center'>
            <Eyebrow className='text-[#9cc3f5]'>{m.team_inspectors_eyebrow()}</Eyebrow>
            <h2 className='mb-5 text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
              {m.team_inspectors_title()}
            </h2>
            <p className='mb-7 text-[17px] leading-[1.7] text-[#c8d3e2]'>
              <ParaglideMessage
                message={m.team_inspectors_text}
                inputs={{ ports: stats.ports.toLocaleString(languageTag()) }}
                markup={{
                  link: ({ children }) => (
                    <Link to='/services' hash='where-we-work' className='underline underline-offset-4 hover:text-white'>
                      {children}
                    </Link>
                  ),
                }}
              />
            </p>
            <ul className='flex flex-col gap-3.5'>
              {inspectorPoints.map((point) => (
                <li key={point()} className='flex items-start gap-3'>
                  <CheckIcon className='mt-0.5 size-5 shrink-0 text-[#9cc3f5]' />
                  {point()}
                </li>
              ))}
            </ul>
            <Link
              to='/careers'
              className='text-navy mt-8 inline-flex min-h-12 items-center gap-2.5 rounded-full bg-white px-6.5 font-semibold'
            >
              {m.team_join()} <ArrowRightIcon className='size-4.5' />
            </Link>
          </div>
          <Photo
            src={SeafarerOnDeckImg}
            alt={m.team_inspectors_image()}
            position='50% 60%'
            className='min-h-90 min-w-0 flex-[1_1_420px]'
          />
        </div>
      </section>

      <ContactCta />
    </>
  );
}
